import { useEffect, useState } from "react";
import { Badge, Button, Modal } from "react-bootstrap";
import { fetchWithAuth } from "../api/fetchWithAuth";

const resultOptions = [
  { value: "passed", label: "Passed", variant: "success" },
  { value: "failed", label: "Failed", variant: "danger" },
  { value: "blocked", label: "Blocked", variant: "warning" },
  { value: "not_run", label: "Not run", variant: "secondary" },
  { value: "inconclusive", label: "Inconclusive", variant: "info" }
];

function formatDateTime(value) {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";

  return date.toLocaleString("bg-BG", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit"
  });
}

function getResultOption(result) {
  return resultOptions.find((option) => option.value === result) ?? {
    value: result,
    label: result,
    variant: "secondary"
  };
}

function ResultBadge({ result }) {
  const option = getResultOption(result);
  return <Badge bg={option.variant}>{option.label}</Badge>;
}

export default function TestCaseRunsModal({ show, onClose, testCase }) {
  const [runs, setRuns] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!show) {
      setRuns([]);
      setError("");
      setLoading(false);
      return;
    }

    let cancelled = false;

    const loadRuns = async () => {
      if (!testCase?.id) return;

      setLoading(true);
      setError("");

      try {
        const res = await fetchWithAuth(`/api/test-cases/${testCase.id}/runs`);

        if (!res.ok) {
          const responseError = await res.json().catch(() => null);
          throw new Error(responseError?.error || "Failed to load runs");
        }

        const data = await res.json();
        if (!cancelled) {
          setRuns(data.runs ?? []);
        }
      } catch (err) {
        if (!cancelled) {
          setRuns([]);
          setError(err.message);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    loadRuns();

    return () => {
      cancelled = true;
    };
  }, [show, testCase]);

  return (
    <Modal show={show} onHide={onClose} size="xl">
      <Modal.Header closeButton>
        <Modal.Title>Runs / Reports - {testCase?.number}</Modal.Title>
      </Modal.Header>

      <Modal.Body>
        {error && (
          <div className="alert alert-danger py-2">{error}</div>
        )}

        <div className="d-flex justify-content-between align-items-center gap-2 mb-3 flex-wrap">
          <div className="text-muted">{testCase?.name}</div>
          <span className="badge text-bg-secondary">Total: {runs.length}</span>
        </div>

        {loading ? (
          <div>Loading...</div>
        ) : (
          <div className="table-responsive" style={{ maxHeight: "58vh", overflowY: "auto" }}>
            <table className="table table-sm table-hover align-middle mb-0">
              <thead className="table-light">
                <tr>
                  <th style={{ position: "sticky", top: 0, width: "130px" }}>Version</th>
                  <th style={{ position: "sticky", top: 0, width: "130px" }}>Device</th>
                  <th style={{ position: "sticky", top: 0, width: "80px" }}>Run #</th>
                  <th style={{ position: "sticky", top: 0, width: "110px" }}>Result</th>
                  <th style={{ position: "sticky", top: 0, width: "170px" }}>Executed</th>
                  <th style={{ position: "sticky", top: 0, width: "110px" }}>Source</th>
                  <th style={{ position: "sticky", top: 0, width: "110px" }}>Duration</th>
                  <th style={{ position: "sticky", top: 0, width: "120px" }}>Created by</th>
                  <th style={{ position: "sticky", top: 0 }}>Note / Log</th>
                </tr>
              </thead>
              <tbody>
                {runs.length === 0 ? (
                  <tr>
                    <td colSpan="9" className="text-center text-muted py-3">
                      No runs found.
                    </td>
                  </tr>
                ) : runs.map((run, index) => (
                  <tr
                    key={run.id}
                    style={{ "--bs-table-bg": index % 2 === 0 ? "rgba(25, 135, 84, 0.06)" : "#ffffff" }}
                  >
                    <td>{run.Version?.name ?? "-"}</td>
                    <td>{run.Device?.name ?? "-"}</td>
                    <td>{run.runNo}</td>
                    <td><ResultBadge result={run.result} /></td>
                    <td>{formatDateTime(run.executedAt)}</td>
                    <td>{run.source}</td>
                    <td>{run.durationMs === null || run.durationMs === undefined ? "-" : run.durationMs}</td>
                    <td>{run.CreatedBy?.name ?? "-"}</td>
                    <td>
                      {run.note && <div>{run.note}</div>}
                      {run.log && (
                        <details className="mt-1">
                          <summary className="text-primary" style={{ cursor: "pointer" }}>Log</summary>
                          <pre className="small bg-light border rounded p-2 mt-2 mb-0" style={{ whiteSpace: "pre-wrap" }}>
                            {run.log}
                          </pre>
                        </details>
                      )}
                      {!run.note && !run.log && <span className="text-muted">-</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Modal.Body>

      <Modal.Footer>
        <Button variant="secondary" onClick={onClose}>
          Close
        </Button>
      </Modal.Footer>
    </Modal>
  );
}
